import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import RecordsHeaderBar from '../components/RecordsHeaderBar';

const SKILL_URL = window.location.origin + '/skill.zip';
const BASE_DOMAIN = window.location.hostname;
const CLAUDE_SKILLS_URL = 'https://claude.ai/customize/skills';

const CLAUDE_WEB_MESSAGE = '/health-record-assistant';
const CLI_MESSAGE = 'I downloaded health-record-assistant.zip, the AI Connector, to my Downloads folder. Install or use it, then help me fetch my health records.';
const CLI_WEB_MESSAGE = `Download the AI Connector from ${SKILL_URL}, install or use it, then help me fetch my health records.`;

type ConnectorTarget = 'claude' | 'cli';

export default function HomePage() {
  const [connectorOpen, setConnectorOpen] = useState(false);
  const [connectorTarget, setConnectorTarget] = useState<ConnectorTarget>('claude');
  const [copiedTarget, setCopiedTarget] = useState<string | null>(null);
  const [showCliDirect, setShowCliDirect] = useState(false);

  const handleCopy = useCallback((target: string, message: string) => {
    navigator.clipboard.writeText(message).then(() => {
      setCopiedTarget(target);
      setTimeout(() => setCopiedTarget(null), 2000);
    });
  }, []);

  const openConnector = useCallback((target: ConnectorTarget) => {
    setConnectorTarget(target);
    setConnectorOpen(true);
  }, []);

  return (
    <div className="page-top with-records-nav">
      <RecordsHeaderBar current="about" />
      <div className="home-header">
        <h1>Health Skillz</h1>
        <p>Connect, collect, and securely share your health records with AI.</p>
        <div className="home-quick-actions">
          <Link to="/records" className="btn btn-primary">Go to My Records</Link>
          <Link to="/records/add" className="btn btn-secondary">Connect to new provider</Link>
        </div>
      </div>

      <div className="container home-container">
        <div className="card home-section">
          <h2>Choose a workflow</h2>

          <div className="workflow-options">
            <section className="workflow-card">
              <div className="workflow-card-topline">
                <div className="workflow-title">Health Records Bundle</div>
                <span className="workflow-pill warning">Contains records</span>
              </div>
              <p>
                Best when you already have records in this browser and want to hand one zip file
                to an AI tool for analysis.
              </p>
              <div className="workflow-actions">
                <Link className="btn btn-primary" to="/records">
                  Go to My Records
                </Link>
              </div>
              <p className="workflow-note">
                From My Records, select what to include, download the bundle, or add more records if needed.
                Store and share the bundle like a medical record export.
              </p>
            </section>

            <section className={`workflow-card${connectorOpen ? ' active' : ''}`}>
              <div className="workflow-card-topline">
                <div className="workflow-title">AI Connector</div>
                <span className="workflow-pill">No records in zip</span>
              </div>
              <p>
                Best when you want Claude.ai, Claude Code, Codex CLI, or another agent to guide
                the collection flow. The AI creates a secure link; you choose what to share.
              </p>
              <div className="workflow-actions">
                <button
                  className={`btn ${connectorTarget === 'claude' && connectorOpen ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => openConnector('claude')}
                >
                  Set up for Claude.ai
                </button>
                <button
                  className={`btn ${connectorTarget === 'cli' && connectorOpen ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => openConnector('cli')}
                >
                  Set up for CLI
                </button>
              </div>

              {connectorOpen && (
                <section className="connector-setup" aria-label="AI Connector setup">
                  <div className="connector-setup-header">
                    <div>
                      <h3>{connectorTarget === 'claude' ? 'Claude.ai setup' : 'CLI agent setup'}</h3>
                      <p>
                        {connectorTarget === 'claude'
                          ? 'Download the connector, upload it in Claude.ai, then start a chat.'
                          : 'Download the connector and give the prompt to your local agent.'}
                      </p>
                    </div>
                    <button className="link" onClick={() => setConnectorOpen(false)}>Hide</button>
                  </div>

                  {connectorTarget === 'claude' ? (
                    <>
                      <ol className="transfer-steps">
                        <li>
                          <a href="/skill.zip" download="health-record-assistant.zip">Download AI Connector</a>.
                        </li>
                        <li>
                          Open <a href={CLAUDE_SKILLS_URL} target="_blank" rel="noopener">Claude Skills</a>.
                        </li>
                        <li>Click the plus button and choose <strong>Upload a skill</strong>.</li>
                        <li>Select the downloaded <code>health-record-assistant.zip</code>.</li>
                        <li>Start a new Claude chat and paste this:</li>
                      </ol>
                      <div className="copy-box" onClick={() => handleCopy('claude-web', CLAUDE_WEB_MESSAGE)}>
                        {CLAUDE_WEB_MESSAGE}
                      </div>
                      <button
                        className="copy-button-inline"
                        onClick={() => handleCopy('claude-web', CLAUDE_WEB_MESSAGE)}
                      >
                        {copiedTarget === 'claude-web' ? 'Copied' : 'Copy chat prompt'}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="setup-actions">
                        <a className="btn btn-primary" href="/skill.zip" download="health-record-assistant.zip">
                          Download AI Connector
                        </a>
                      </div>
                      <p className="transfer-copy-intro">
                        After the download finishes, paste this into Claude Code, Codex CLI, or another local terminal agent:
                      </p>
                      <div className="copy-box" onClick={() => handleCopy('cli', CLI_MESSAGE)}>
                        {CLI_MESSAGE}
                      </div>
                      <button
                        className="copy-button-inline"
                        onClick={() => handleCopy('cli', CLI_MESSAGE)}
                      >
                        {copiedTarget === 'cli' ? 'Copied' : 'Copy CLI prompt'}
                      </button>
                      <button className="help-toggle" style={{ marginTop: 10 }} onClick={() => setShowCliDirect(!showCliDirect)}>
                        {showCliDirect ? '▾' : '▸'} Direct-download prompt for agents with web access
                      </button>
                      {showCliDirect && (
                        <div className="help-detail">
                          <p>
                            Some CLI agents can download directly from <code>{BASE_DOMAIN}</code> without a manual file step.
                          </p>
                          <div className="copy-box" onClick={() => handleCopy('cli-web', CLI_WEB_MESSAGE)}>
                            {CLI_WEB_MESSAGE}
                          </div>
                          <button
                            className="copy-button-inline"
                            onClick={() => handleCopy('cli-web', CLI_WEB_MESSAGE)}
                          >
                            {copiedTarget === 'cli-web' ? 'Copied' : 'Copy direct-download prompt'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  <p className="workflow-note">
                    After setup, the AI sends you a secure link. You choose records in the browser, and the browser
                    encrypts them end-to-end before sending.
                  </p>
                </section>
              )}
            </section>
          </div>

        </div>
      </div>
    </div>
  );
}
